import React, { useCallback, useMemo, useState } from 'react';
import DialogWindow from '../../../../Elements/DialogWindow/DialogWindow';
import DialogCloseButton from '../../../../Elements/DialogWindow/DialogCloseButton';
import FigmaButton from '../../../../Elements/FigmaButton';
import ButtonColorsEnum from '../../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../../Enums/ButtonTypeEnum';
import InvoiceAutomationService from '../../../../Services/InvoiceAutomationService';
import useBackendData from '../../../../Helpers/useBackendData';
import { getRunChoices } from '../../../../Helpers/invoiceAutomationRunHelper';
import InvoiceAutomationRunPreview from './InvoiceAutomationRunPreview';
import InvoiceAutomationRunResult from './InvoiceAutomationRunResult';
import useInvoiceAutomationRun from './useInvoiceAutomationRun';

// Run now: says what the run is about to do and offers to bring the out-of-scope jobs into it, a line at a time,
// working the run out again whenever a line is ticked; then runs the project's automatic invoicing and shows what it
// did.
export default function InvoiceAutomationRunNow({ projectId = null, projectName = '', onClose = () => {} }) {
  const [tickedGroupKeys, setTickedGroupKeys] = useState([]);
  const runChoices = useMemo(() => getRunChoices(tickedGroupKeys), [tickedGroupKeys]);
  const loadPreview = useCallback(
    () => InvoiceAutomationService.GetInvoiceAutomationRunPreview(projectId, runChoices),
    [projectId, runChoices]
  );
  // A preview that is not the answer for the current ticks (isPreviewStale) stays on screen, but Run waits for one
  // that is: Run would send the current ticks, not those the preview shows.
  const {
    data: preview,
    error: previewError,
    isLoading: isPreviewLoading,
    isStale: isPreviewStale,
  } = useBackendData(loadPreview, 'Error! ErrorCode: IARN23. Failed to work out what the run would do.');
  const { run, isRunning, result, skippedJobs, removedJobs, runError } = useInvoiceAutomationRun(projectId);
  const cannotStart = preview === null && previewError !== null;

  function renderBody() {
    if (result !== null) {
      return (
        <InvoiceAutomationRunResult
          result={result}
          skippedJobs={skippedJobs}
          removedJobs={removedJobs}
        />
      );
    }
    if (cannotStart) {
      return (
        <div className='iauto-run'>
          <p className='iauto-run-lead'>The run cannot start.</p>
        </div>
      );
    }
    return (
      <InvoiceAutomationRunPreview
        preview={preview}
        isUpdating={isPreviewLoading}
        isOutdated={isPreviewStale}
        tickedGroupKeys={tickedGroupKeys}
        onTickedGroupKeysChange={setTickedGroupKeys}
      />
    );
  }

  function renderButtons() {
    if (result !== null || cannotStart) {
      return <DialogCloseButton onClick={onClose} />;
    }
    return (
      <>
        <DialogCloseButton
          label='Cancel'
          disabled={isRunning}
          onClick={onClose}
        />
        <FigmaButton
          label='Run'
          type={ButtonTypeEnum.NormalNormal}
          color={ButtonColorsEnum.Primary}
          disabled={preview === null || isPreviewLoading || isPreviewStale || isRunning}
          onClick={() => run(runChoices)}
        />
      </>
    );
  }

  return (
    <DialogWindow
      title='Run Automatic Invoicing Now'
      eyebrow={projectName}
      iconName='fa-solid fa-play'
      errorText={runError ?? previewError}
      buttons={renderButtons()}
      onClose={onClose}
    >
      {renderBody()}
    </DialogWindow>
  );
}
